import type {
  HttpTransport,
  TransportRequestConfig,
} from '@mangovoice/api-client'

/**
 * Creates a scoped transport that prefixes all request paths with a base URL.
 * Useful for domain libraries that define relative paths against a specific API prefix.
 *
 * Auth inheritance: The scoped transport delegates to the parent transport, so it
 * inherits all interceptors (auth, retry, rate-limit) configured on the parent.
 * Requests through a scoped transport ARE authenticated if the parent transport is.
 */
export function createScopedTransport(
  transport: HttpTransport,
  baseUrl: string,
): HttpTransport {
  return {
    get: <T>(
      path: string,
      params?: Record<string, unknown>,
      config?: TransportRequestConfig,
    ) => transport.get<T>(`${baseUrl}${path}`, params, config),

    post: <T>(path: string, data?: unknown, config?: TransportRequestConfig) =>
      transport.post<T>(`${baseUrl}${path}`, data, config),

    put: <T>(path: string, data?: unknown, config?: TransportRequestConfig) =>
      transport.put<T>(`${baseUrl}${path}`, data, config),

    patch: <T>(path: string, data?: unknown, config?: TransportRequestConfig) =>
      transport.patch<T>(`${baseUrl}${path}`, data, config),

    delete: <T>(
      path: string,
      params?: Record<string, unknown>,
      config?: TransportRequestConfig,
    ) => transport.delete<T>(`${baseUrl}${path}`, params, config),
  }
}

/**
 * AI Configuration resource — scoped transport with default base path.
 */
export function createAiConfigurationResource(
  transport: HttpTransport,
  baseUrl?: string,
) {
  return createScopedTransport(transport, baseUrl ?? '/app/v1/ai-configuration')
}
